/**
 * Device-to-device key transfer — HPKE (RFC 9180) across two QR codes.
 *
 * Protocol v3. The device that *needs* the key speaks first:
 *
 *   target (new device)                  source (old device, holds the seed)
 *   ───────────────────                  ───────────────────────────────────
 *   createTransferRequest()
 *     → QR #1 { v, r, t }         ──▶    sealSeedForRequest(qr1, seed)
 *                                          → QR #2 { v, e, ct, t }
 *   openSealedSeed(qr2, keyPair)  ◀──
 *     → { seed, confirmationCode }         → confirmationCode
 *
 * ⚠️  The seed never appears in a QR code in any form a camera can use, and that
 *     is the entire point of the two-code shape. QR #1 carries an ephemeral
 *     X25519 *public* key; QR #2 carries a ciphertext sealed to it. The matching
 *     private key is generated on the target device and never leaves its memory,
 *     so photographing either code — or both — yields nothing.
 *
 *     v2 did the opposite: it wrapped the seed with a fresh AES key and then put
 *     that key in the same JSON payload, as field `ok`. The wrapping was a no-op
 *     and the QR was a plaintext export of the account's E2EE master seed,
 *     readable with a stock QR reader and `JSON.parse` — no app, no account, no
 *     network. Do not reintroduce any field that travels alongside the
 *     ciphertext it unlocks.
 *
 * Both devices independently derive a 6-digit confirmation code from the HPKE
 * context. Matching codes tell the user the two screens are talking to each
 * other rather than one of them to a QR an attacker substituted. This is a Short
 * Authentication String in the sense of ZRTP (RFC 6189).
 *
 * Ciphersuite: DHKEM(X25519, HKDF-SHA256) / HKDF-SHA256 / AES-128-GCM — the suite
 * RFC 9180 publishes test vectors for in Appendix A.1, which `key-transfer.test.ts`
 * checks against. AES-128 is not a downgrade here: X25519 caps the construction
 * at ~128-bit security whatever the AEAD, so AES-256-GCM never bought more.
 */

import * as HPKE from 'hpke';
import {
  KEM_DHKEM_X25519_HKDF_SHA256,
  KDF_HKDF_SHA256,
  AEAD_AES_128_GCM,
} from '@panva/hpke-noble';
import { arrayBufferToBase64, base64ToUint8Array } from './encoding';

/** How long a transfer QR code stays valid, in seconds. Enforced by the parser
 *  on both sides — not merely displayed. */
export const QR_EXPIRY_SECONDS = 300;

/** Allowance for the two devices' clocks disagreeing. A payload stamped further
 *  into the future than this is refused rather than silently trusted. */
const CLOCK_SKEW_TOLERANCE_MS = 60_000;

const TRANSFER_VERSION = 3;
const X25519_PUBLIC_KEY_BYTES = 32;
const SEED_BYTES = 32;

/** HPKE `info` — binds the context to this protocol and version. */
const HPKE_INFO = utf8Bytes('cynosure/key-transfer/v3');

/** HPKE exporter context for the confirmation code. */
const SAS_CONTEXT = utf8Bytes('cynosure/sas/v3');

/**
 * Exported so `key-transfer.test.ts` can run RFC 9180's Appendix A.1 vectors
 * against the suite this module actually uses. Changing any of the three
 * factories below makes those vectors fail, which is the point — the ciphersuite
 * is part of the wire format and cannot drift silently.
 */
export const transferSuite = new HPKE.CipherSuite(
  KEM_DHKEM_X25519_HKDF_SHA256,
  KDF_HKDF_SHA256,
  AEAD_AES_128_GCM,
);

const suite = transferSuite;

/** Wire format of QR #1 — the target device's request for the seed. */
interface RequestPayload {
  v: number;
  /** Ephemeral X25519 public key, base64. */
  r: string;
  /** Issued-at, epoch milliseconds. */
  t: number;
}

/** Wire format of QR #2 — the seed sealed to the target's ephemeral key. */
interface ResponsePayload {
  v: number;
  /** HPKE encapsulated key, base64. */
  e: string;
  /** HPKE ciphertext, base64. */
  ct: string;
  /** Issued-at, epoch milliseconds. Authenticated as AEAD associated data. */
  t: number;
}

export interface TransferRequest {
  /** JSON to render as QR #1. */
  qr: string;
  /** Ephemeral key pair. Hold in memory for the life of the screen; never
   *  persist it and never put it on the wire. */
  keyPair: HPKE.KeyPair;
}

export interface SealedSeed {
  /** JSON to render as QR #2. */
  qr: string;
  /** 6 digits to show the user alongside it. */
  confirmationCode: string;
}

export interface OpenedSeed {
  seed: Uint8Array;
  /** 6 digits to show the user, who compares them against the source device. */
  confirmationCode: string;
}

// ── Target device (needs the key) ────────────────────────────────────────────

/**
 * Begin a transfer. Call on the device that has no key yet, and render `qr`.
 */
export async function createTransferRequest(): Promise<TransferRequest> {
  const keyPair = await suite.GenerateKeyPair(true);
  const publicKey = await suite.SerializePublicKey(keyPair.publicKey);

  const payload: RequestPayload = {
    v: TRANSFER_VERSION,
    r: arrayBufferToBase64(publicKey),
    t: Date.now(),
  };

  return { qr: JSON.stringify(payload), keyPair };
}

/**
 * Finish a transfer: open QR #2 with the key pair from `createTransferRequest`.
 * Throws if the payload is malformed, expired, sealed to a different device, or
 * tampered with.
 */
export async function openSealedSeed(
  responseQr: string,
  keyPair: HPKE.KeyPair
): Promise<OpenedSeed> {
  const payload = parseJson<ResponsePayload>(responseQr);
  assertVersion(payload.v);

  const encapsulatedSecret = decodeBase64(payload.e, null, 'encapsulated key');
  const ciphertext = decodeBase64(payload.ct, null, 'encrypted key');
  assertFresh(payload.t, 'transfer code');

  const publicKey = await suite.SerializePublicKey(keyPair.publicKey);
  const aad = associatedData(payload.t, publicKey);

  let context: HPKE.RecipientContext;
  let seed: Uint8Array;
  try {
    context = await suite.SetupRecipient(keyPair, encapsulatedSecret, {
      info: HPKE_INFO,
    });
    seed = await context.Open(ciphertext, aad);
  } catch {
    // Sealed to a different device, edited timestamp, or a corrupted scan —
    // indistinguishable by design, and all three mean the same thing here.
    throw new Error(
      'Could not read this transfer code. Make sure you scanned the code shown on the device you started from, then try again.'
    );
  }

  if (seed.length !== SEED_BYTES) {
    throw new Error('Transfer code carried an unexpected key length.');
  }

  return { seed, confirmationCode: await confirmationCode(context) };
}

// ── Source device (holds the key) ────────────────────────────────────────────

/**
 * Seal the seed for whoever produced `requestQr`. Call on the device that
 * already has the key, and render the returned `qr` plus `confirmationCode`.
 */
export async function sealSeedForRequest(
  requestQr: string,
  seed: Uint8Array
): Promise<SealedSeed> {
  if (seed.length !== SEED_BYTES) {
    throw new Error('Encryption key is not in the expected format.');
  }

  const payload = parseJson<RequestPayload>(requestQr);
  assertVersion(payload.v);

  const targetPublicKey = decodeBase64(
    payload.r,
    X25519_PUBLIC_KEY_BYTES,
    'device key'
  );
  assertFresh(payload.t, 'request code');

  let publicKey: HPKE.Key;
  try {
    publicKey = await suite.DeserializePublicKey(targetPublicKey);
  } catch {
    throw new Error('Transfer code has a malformed device key.');
  }

  const { encapsulatedSecret, ctx } = await suite.SetupSender(publicKey, {
    info: HPKE_INFO,
  });

  const issuedAt = Date.now();
  const ciphertext = await ctx.Seal(seed, associatedData(issuedAt, targetPublicKey));

  const response: ResponsePayload = {
    v: TRANSFER_VERSION,
    e: arrayBufferToBase64(encapsulatedSecret),
    ct: arrayBufferToBase64(ciphertext),
    t: issuedAt,
  };

  return {
    qr: JSON.stringify(response),
    confirmationCode: await confirmationCode(ctx),
  };
}

// ── Derivation ───────────────────────────────────────────────────────────────

/**
 * Associated data for the AEAD: protocol version, issue time, and the target's
 * public key.
 *
 * This is what makes expiry real rather than decorative. `t` is authenticated,
 * so editing it to defeat the freshness check produces a different AAD and
 * `Open()` refuses the ciphertext outright.
 */
function associatedData(issuedAt: number, targetPublicKey: Uint8Array): Uint8Array {
  return utf8Bytes(
    `${TRANSFER_VERSION}|${issuedAt}|${arrayBufferToBase64(targetPublicKey)}`
  );
}

/**
 * Six digits both devices derive independently, via HPKE's exporter interface.
 * They agree only if the two sides established the same context.
 */
async function confirmationCode(
  context: HPKE.SenderContext | HPKE.RecipientContext
): Promise<string> {
  const bytes = await context.Export(SAS_CONTEXT, 4);
  const n =
    ((bytes[0] << 24) | (bytes[1] << 16) | (bytes[2] << 8) | bytes[3]) >>> 0;
  return (n % 1_000_000).toString().padStart(6, '0');
}

// ── Parsing helpers ──────────────────────────────────────────────────────────
//
// Everything below reads data a camera handed us, so nothing is assumed about
// its shape.

function parseJson<T>(raw: string): T {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    throw new Error('That is not a Cynosure transfer code.');
  }
  if (typeof parsed !== 'object' || parsed === null) {
    throw new Error('That is not a Cynosure transfer code.');
  }
  return parsed as T;
}

function assertVersion(version: unknown): void {
  if (version === TRANSFER_VERSION) return;
  throw new Error(
    'This transfer code was made by a different version of the app. Update Cynosure on both devices and try again.'
  );
}

function assertFresh(issuedAt: unknown, label: string): void {
  if (typeof issuedAt !== 'number' || !Number.isFinite(issuedAt)) {
    throw new Error('That is not a Cynosure transfer code.');
  }

  const age = Date.now() - issuedAt;
  if (age > QR_EXPIRY_SECONDS * 1000) {
    throw new Error(
      `This ${label} has expired. Generate a new one on the other device.`
    );
  }
  if (age < -CLOCK_SKEW_TOLERANCE_MS) {
    throw new Error(
      `This ${label} is dated in the future. Check the date and time settings on both devices.`
    );
  }
}

function decodeBase64(
  value: unknown,
  expectedLength: number | null,
  label: string
): Uint8Array {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error('That is not a Cynosure transfer code.');
  }

  let bytes: Uint8Array;
  try {
    bytes = base64ToUint8Array(value);
  } catch {
    throw new Error('That is not a Cynosure transfer code.');
  }

  if (bytes.length === 0 || (expectedLength !== null && bytes.length !== expectedLength)) {
    throw new Error(`Transfer code has a malformed ${label}.`);
  }
  return bytes;
}

function utf8Bytes(text: string): Uint8Array {
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) {
    bytes[i] = text.charCodeAt(i) & 0xff;
  }
  return bytes;
}
